import { useEffect, useState, RefObject } from 'react';
import { Cat, ColumnCategory, NameAndHref, PrimaryCategoryData } from 'types';
import { KEY_CODES } from 'utility/constants';
import { checkStores, setDataEmTag } from 'utility/utility';

type Props = {
  desktopNavData: PrimaryCategoryData[];
  index: number;
  changeFocus: (offset?: number) => void;
  firstSubLinkRef: RefObject<HTMLAnchorElement | null>;
};

const isValidData = (desktopNavData: string | any[], index: number) => {
  return (
    desktopNavData &&
    Array.isArray(desktopNavData) &&
    desktopNavData.length > index &&
    desktopNavData[index]?.csvData &&
    Array.isArray(desktopNavData[index].csvData) &&
    desktopNavData[index].csvData.length > 0 &&
    desktopNavData[index].csvData[0]?.col &&
    Array.isArray(desktopNavData[index].csvData[0].col)
  );
};

const MenuColumns = ({ desktopNavData, index, changeFocus, firstSubLinkRef }: Props) => {
  // creates list of L1's for a given primary category in order to generate the ADA compliant 'skip links'
  const createHeadingArray = (): string[] => {
    // Simple check to ensure that data is available and structured correctly
    if (isValidData(desktopNavData, index)) {
      return desktopNavData[index].csvData[0].col.reduce((all: string[], current: any) => {
        return [
          ...all,
          ...current.cats.map((categoryHeading: NameAndHref) => {
            return categoryHeading.n;
          }),
        ];
      }, []);
    }

    return [];
  };

  const [categoryHeadingArray, setCategoryHeadingArray] = useState<string[]>([]);

  useEffect(() => {
    setCategoryHeadingArray(createHeadingArray());
  }, [index]);

  const handleColumnCategoryKeyDown = (
    event: React.KeyboardEvent,
    columnIndex: number,
    columnCategoryIndex: number,
  ): void => {
    if (event.key === KEY_CODES.ESCAPE) {
      changeFocus();
    }
    // shift + tab
    else if (event.shiftKey && event.key === KEY_CODES.TAB) {
      if (columnIndex === 0 && columnCategoryIndex === 0) {
        changeFocus(1);
      }
    }
  };

  const createSkipLink = (columnName: string): string | undefined => {
    const currentIndex = categoryHeadingArray.indexOf(columnName);
    if (currentIndex !== categoryHeadingArray.length - 1) {
      return `${categoryHeadingArray[currentIndex + 1]}`;
    }
    return undefined;
  };

  const sublinks = (columnName: string, columnCategory: ColumnCategory[]) => {
    const skipLink = createSkipLink(columnName);
    return (
      <>
        <a
          className="desktop-nav-container__menu__column__category--sublinks skip-link hmf-mb-xs"
          href={`#${skipLink}`}
          onKeyDown={(event) => {
            if (event.key === KEY_CODES.ESCAPE) {
              changeFocus();
            } else if (event.key === KEY_CODES.SPACE) {
              event.preventDefault();
              window.location.assign(`/#${skipLink}`);
            }
          }}>
          Skip To {skipLink}
        </a>
        {columnCategory.map((category: Cat) => (
          <a
            key={category.n}
            className="sublinks hmf-subheader-m hmf-mb-xs"
            href={checkStores(category.u)}
            onKeyDown={(event) => {
              if (event.key === KEY_CODES.ESCAPE) {
                changeFocus();
              }
            }}
            data-em={setDataEmTag('TopNav', desktopNavData[index].title, columnName, category.n)}>
            {category.n}
          </a>
        ))}
      </>
    );
  };

  return (
    <>
      {isValidData(desktopNavData, index) &&
        desktopNavData[index].csvData[0].col.map((column, columnIndex) => {
          return (
            <div
              className="desktop-nav-container__menu__column"
              tabIndex={-1}
              key={`column-${column.cats[0].n}`}>
              {column?.cats
                ? column.cats.map((columnCategory: ColumnCategory, columnCategoryIndex: number) => {
                    return (
                      <ul
                        key={`${columnCategory.n}-list`}
                        className="desktop-nav-container__menu__column__category hmf-mb-m">
                        <a
                          className="desktop-nav-container__menu__column__category--heading hmf-header-bold-s hmf-mb-s"
                          href={checkStores(columnCategory.u)}
                          onKeyDown={(event) =>
                            handleColumnCategoryKeyDown(event, columnIndex, columnCategoryIndex)
                          }
                          ref={
                            columnIndex === 0 && columnCategoryIndex === 0 ? firstSubLinkRef : null
                          }
                          id={`${columnCategory.n}`}
                          data-em={setDataEmTag(
                            'TopNav',
                            desktopNavData[index].title,
                            columnCategory.n,
                          )}>
                          {columnCategory.n}
                        </a>
                        {columnCategory.ch &&
                          columnCategory.ch.length > 0 &&
                          sublinks(columnCategory.n, columnCategory.ch)}
                      </ul>
                    );
                  })
                : null}
            </div>
          );
        })}
    </>
  );
};

export default MenuColumns;
